import { Settings, Web3DViewer } from '@technology/web3d';
import type { Accessor } from 'solid-js';
import { CompassPlugin } from '@technology/web3d-plugin-compass';
import { GridPlugin } from '@technology/web3d-plugin-grid';
import { IconsPlugin } from '@technology/web3d-plugin-icons';
import {
  ControlPointType,
  LineEditingPlugin,
} from '@technology/web3d-plugin-line-editing';
import { MarkupPlugin } from '@technology/web3d-plugin-markup';
import { PanoramaPlugin } from '@technology/web3d-plugin-panorama';
import { PlanarPlugin } from '@technology/web3d-plugin-planar';
import { PotreeManagerPlugin } from '@technology/web3d-plugin-potreemanager';
import { Color, ThreejsPlugin } from '@technology/web3d-plugin-threejs';
import { TileMapPlugin } from '@technology/web3d-plugin-tilemap';
import { TrimbimPlugin } from '@technology/web3d-plugin-trimbim';
import { GltfPlugin } from '@technology/web3d-plugin-gltf';
import { QuantizedMeshPlugin } from '@trimble/web3d-plugin-quantized-mesh';
import { TextEditingPlugin } from '@trimble/web3d-plugin-text-editing';
import { LODSetting } from './lod-setting';
import { exposeWeb3dModelTypesForE2e } from './web3d-model-types';
import { Tiles3DPlugin } from '@technology/web3d-plugin-3dtiles';
import { FeaturesServicePlugin } from '@trimble/web3d-plugin-features-service';
import { normalizePathSegments } from './path-segments';

interface ViewerPlugins {
  compassPlugin: CompassPlugin;
  iconPlugin: IconsPlugin;
  panoramaPlugin: PanoramaPlugin;
  planarPlugin: PlanarPlugin;
  potreeManager: PotreeManagerPlugin;
  tileMapPlugin: TileMapPlugin;
  markupPlugin: MarkupPlugin;
  lineEditingPlugin: LineEditingPlugin;
  textEditingPlugin: TextEditingPlugin;
  gridPlugin: GridPlugin;
  threejsPlugin: ThreejsPlugin;
  gltfPlugin: GltfPlugin;
  quantizedMeshPlugin: QuantizedMeshPlugin;
  tiles3DPlugin: Tiles3DPlugin;
  featuresServicePlugin: FeaturesServicePlugin;
}

export interface Web3DViewerWrapper extends ViewerPlugins {
  viewer: Web3DViewer;
  /**
   * When set, shared alignment measurement only attaches pick handling to this viewer while true.
   * Omit in single-viewer apps (defaults to true).
   */
  isAlignmentMeasurementInteractionAllowed?: Accessor<boolean>;
  /**
   * When set, freeline/draw markup creation is only enabled on this viewer while true.
   * Display/replication of draw markups is unaffected. Omit to allow creation (default true).
   */
  isDrawMarkupCreationAllowed?: Accessor<boolean>;
}

/** Resolves {@link Web3DViewerWrapper.isAlignmentMeasurementInteractionAllowed}; defaults to true when unset. */
export function isAlignmentMeasurementInteractionAllowedForWrapper(
  viewerWrapper: Web3DViewerWrapper
): boolean {
  return viewerWrapper.isAlignmentMeasurementInteractionAllowed?.() ?? true;
}

/** Resolves {@link Web3DViewerWrapper.isDrawMarkupCreationAllowed}; defaults to true when unset. */
export function isDrawMarkupCreationAllowedForWrapper(
  viewerWrapper: Web3DViewerWrapper
): boolean {
  return viewerWrapper.isDrawMarkupCreationAllowed?.() ?? true;
}

export function toLeadingSlashPath(pathOrUrl: string): string {
  // Wasm paths in PotreeManagerPlugin don't work with trailing slash.
  // Leading slash is required
  const stripped = pathOrUrl.endsWith('/') ? pathOrUrl.slice(0, -1) : pathOrUrl;
  const pathOnly =
    stripped.startsWith('http://') || stripped.startsWith('https://')
      ? new URL(stripped).pathname.replace(/\/$/, '')
      : stripped;
  return pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
}

function ensureTrailingSlash(value: string): string {
  return value.endsWith('/') ? value : `${value}/`;
}

function assertAllowedWeb3dPath(web3dPath: string): void {
  if (web3dPath.startsWith('//')) {
    throw new Error(
      'Invalid web3d path: protocol-relative URLs are not allowed'
    );
  }

  const schemeMatch = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.exec(web3dPath);
  if (
    schemeMatch &&
    !web3dPath.startsWith('http://') &&
    !web3dPath.startsWith('https://')
  ) {
    throw new Error('Invalid web3d path: only http(s) URLs are allowed');
  }
}

export function resolveDeployWeb3dPath(
  web3dPath: string,
  basePath: string = '/'
): string {
  if (!web3dPath) {
    return web3dPath;
  }

  assertAllowedWeb3dPath(web3dPath);

  if (web3dPath.startsWith('http://') || web3dPath.startsWith('https://')) {
    return ensureTrailingSlash(web3dPath);
  }

  if (web3dPath.startsWith('/')) {
    return ensureTrailingSlash(normalizePathSegments(web3dPath));
  }

  const base = ensureTrailingSlash(basePath || '/');
  const relative = web3dPath.replace(/^\/+/, '');
  return ensureTrailingSlash(normalizePathSegments(`${base}${relative}`));
}

function addPlugins(viewer: Web3DViewer, web3DPath: string): ViewerPlugins {
  const compassPlugin = new CompassPlugin();
  viewer.addPlugin(compassPlugin);

  const potreeManager = new PotreeManagerPlugin({
    wasmPath: toLeadingSlashPath(web3DPath),
  });
  viewer.addPlugin(potreeManager);

  const panoramaPlugin = new PanoramaPlugin();
  viewer.addPlugin(panoramaPlugin);

  const planarPlugin = new PlanarPlugin();
  viewer.addPlugin(planarPlugin);

  const iconPlugin = new IconsPlugin({ batchSize: 2000 });
  viewer.addPlugin(iconPlugin);

  const trimBim = new TrimbimPlugin({
    workerUrl: web3DPath,
  });
  viewer.addPlugin(trimBim);
  trimBim.settings.lodSetting =
    LODSetting.Optimal as unknown as (typeof trimBim.settings)['lodSetting'];

  const tileMapPlugin = new TileMapPlugin();
  viewer.addPlugin(tileMapPlugin);

  const markupPlugin = new MarkupPlugin();
  viewer.addPlugin(markupPlugin);

  const lineEditingPlugin = new LineEditingPlugin({
    controlPointType: ControlPointType.Drag,
    hideTransformHandlesForSingleLine: true,
    keybinds: {
      ['Undo']: 'NO_KEY',
      ['Redo']: 'NO_KEY',
    },
  });
  viewer.addPlugin(lineEditingPlugin);

  const textEditingPlugin = new TextEditingPlugin();
  viewer.addPlugin(textEditingPlugin);

  const gridPlugin = new GridPlugin();
  viewer.addPlugin(gridPlugin);

  const threejsPlugin = new ThreejsPlugin();
  viewer.addPlugin(threejsPlugin);

  const gltfPlugin = new GltfPlugin();
  viewer.addPlugin(gltfPlugin);

  const quantizedMeshPlugin = new QuantizedMeshPlugin();
  viewer.addPlugin(quantizedMeshPlugin);

  const tiles3DPlugin = new Tiles3DPlugin();
  viewer.addPlugin(tiles3DPlugin);

  // Do not add GaussianSplattingPlugin here. Its module imports
  // @sparkjsdev/spark, which starts the Spark WASM runtime on load and
  // breaks apps that never use splats. Attach it from the splat capability
  // provider when a splat is actually loaded.
  const featuresServicePlugin = new FeaturesServicePlugin();
  viewer.addPlugin(featuresServicePlugin);

  return {
    compassPlugin,
    iconPlugin,
    panoramaPlugin,
    planarPlugin,
    potreeManager,
    tileMapPlugin,
    markupPlugin,
    lineEditingPlugin,
    textEditingPlugin,
    gridPlugin,
    threejsPlugin,
    gltfPlugin,
    quantizedMeshPlugin,
    tiles3DPlugin,
    featuresServicePlugin,
  };
}

export interface Create3DViewerSettings {
  readonly fxaa?: boolean;
  readonly msaa?: boolean;
  readonly renderEdges?: boolean;
  readonly ssao?: boolean;
  readonly orderIndependentTransparency?: boolean;
  readonly sandbox?: boolean;
  readonly continuousRendering?: boolean;
  readonly vertexInterpolationMaterials?: boolean;
  readonly transparencyPeelsCount?: number;
  readonly background?: {
    readonly skyColor: string;
    readonly groundColor: string;
  };
}

export function create3DViewer(
  parentElement: HTMLDivElement,
  web3DPath: string,
  viewerSettings?: Create3DViewerSettings
): Web3DViewerWrapper {
  const settings: Partial<Settings> = {
    staticRootUrl: web3DPath,
    fxaa:
      viewerSettings && 'fxaa' in viewerSettings ? viewerSettings.fxaa : false,
    msaa: viewerSettings?.msaa ?? true,
    renderEdges: viewerSettings?.renderEdges ?? false,
    ssao: viewerSettings?.ssao ?? false,
    orderIndependentTransparency:
      viewerSettings?.orderIndependentTransparency ?? false,
    sandbox: viewerSettings?.sandbox ?? false,
    continuousRendering: viewerSettings?.continuousRendering ?? false,
    ...(viewerSettings?.vertexInterpolationMaterials !== undefined && {
      vertexInterpolationMaterials: viewerSettings.vertexInterpolationMaterials,
    }),
    ...(viewerSettings?.transparencyPeelsCount !== undefined && {
      transparencyPeelsCount: viewerSettings.transparencyPeelsCount,
    }),
  };

  if (viewerSettings?.background) {
    const skyColor = new Color(viewerSettings.background.skyColor);
    const groundColor = new Color(viewerSettings.background.groundColor);
    settings.background = { skyColor, groundColor };
  }

  const viewer = new Web3DViewer(settings);

  exposeWeb3dModelTypesForE2e();

  const plugins = addPlugins(viewer, web3DPath);

  parentElement.appendChild(viewer);

  return {
    viewer,
    ...plugins,
  };
}
